"use client";

import { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/components/ui/form-error";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useEditorBackend } from "@/lib/editor-backend/context";

/** Soft client-side cap on the note's visible text. */
const MAX_NOTE_CHARS = 1000;

interface ChapterSettingsDialogProps {
    chapterId: string;
    chapterTitle: string;
    authorNote?: Record<string, unknown> | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Edit a chapter's metadata: title + end-of-chapter author's note.
 * Opened from the sidebar pencil and the editor toolbar. Saving the note on a
 * published chapter takes effect immediately — no re-publish step.
 */
export function ChapterSettingsDialog({
    chapterId,
    chapterTitle,
    authorNote,
    open,
    onOpenChange,
}: ChapterSettingsDialogProps) {
    const backend = useEditorBackend();
    const [title, setTitle] = useState(chapterTitle);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [noteChars, setNoteChars] = useState(0);

    const noteEditor = useEditor({
        immediatelyRender: false,
        extensions: [
            // Notes are asides, not content: no headings/blocks. StarterKit v3's
            // Link stays on with autolink, so pasted URLs (Patreon, Discord…)
            // become links without needing a toolbar.
            StarterKit.configure({
                heading: false,
                blockquote: false,
                codeBlock: false,
                horizontalRule: false,
            }),
            Placeholder.configure({
                placeholder:
                    "Thank readers, announce your schedule, link your Patreon…",
            }),
        ],
        content: (authorNote as object) ?? "",
        onCreate: ({ editor }) => setNoteChars(editor.getText().length),
        onUpdate: ({ editor }) => setNoteChars(editor.getText().length),
        editorProps: {
            attributes: {
                // A <label htmlFor> can't target a contenteditable, so name the
                // editor itself for screen readers.
                "aria-label": "Author's note",
                role: "textbox",
                "aria-multiline": "true",
                class: "min-h-24 max-h-60 overflow-y-auto rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring",
            },
        },
    });

    const overLimit = noteChars > MAX_NOTE_CHARS;

    async function handleSave() {
        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            setError("Chapter title can't be empty");
            return;
        }
        if (overLimit) return;

        setSaving(true);
        setError(null);
        const result = await backend.updateChapterMeta(chapterId, {
            title: trimmedTitle,
            authorNote: noteEditor?.isEmpty
                ? null
                : (noteEditor?.getJSON() as Record<string, unknown>),
        });
        setSaving(false);

        if (result?.error) {
            setError(result.error);
            return;
        }
        toast.success("Chapter updated");
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Edit chapter</DialogTitle>
                    <DialogDescription>
                        The author&apos;s note is shown to readers at the end of
                        this chapter.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <FormError message={error} />
                    <div className="space-y-2">
                        <Label htmlFor="chapter-title">Title</Label>
                        <Input
                            id="chapter-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onKeyDown={(e) => {
                                // Ignore Enter while an IME candidate is composing.
                                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                                    handleSave();
                                }
                            }}
                        />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-baseline justify-between">
                            <Label asChild>
                                <span>
                                    Author&apos;s note
                                    <span className="ml-1 font-normal text-muted-foreground">
                                        (optional)
                                    </span>
                                </span>
                            </Label>
                            <span
                                className={`text-xs ${
                                    overLimit
                                        ? "font-semibold text-destructive"
                                        : "text-muted-foreground"
                                }`}
                            >
                                {noteChars}/{MAX_NOTE_CHARS}
                            </span>
                        </div>
                        <EditorContent editor={noteEditor} />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || overLimit}
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
